/**
 * EM3 Nomad Adapter
 *
 * Implements IOpenNomadAPI for EasyMap 3 integration with Project Nomad.
 * Calls Nomad backend directly (ACN mode) with agency authentication headers.
 *
 * Auth pattern:
 * - EM3 authenticates users via FusionAuth
 * - Adapter sends agency credentials + user info to Nomad
 * - Nomad trusts registered agencies, doesn't validate FusionAuth JWTs
 *
 * @module services/nomad
 */

import type {
  IOpenNomadAPI,
  User,
  UserRole,
  Model,
  ModelStatus,
  ModelCreateParams,
  ModelFilter,
  PaginationParams,
  PaginatedResponse,
  Job,
  JobSubmitResponse,
  JobStatusDetail,
  ModelResults,
  ExportFormat,
  ExportParams,
  WeatherStation,
  FuelTypeData,
  ElevationData,
  Engine,
  AgencyConfig,
  BBox,
  GeoJSONGeometry,
  Unsubscribe,
  MapLayer,
} from './types';
import { resolveWeatherParams } from '@nomad/frontend';
import { getDrawService as getGlobalDrawService, type DrawService } from '../drawing';
import { EM3_ROLES } from '../../types/identity';

// =============================================================================
// Configuration
// =============================================================================

export interface EM3AdapterOptions {
  /** Nomad backend URL (e.g., 'https://nomad.nwtfire.ca/api') */
  nomadApiUrl: string;
  /** Agency identifier */
  agencyId?: string;
  /** Function to get current FusionAuth user info */
  getUserInfo: () => FusionAuthUserInfo | null;
  /** Function to get FusionAuth access token */
  getAccessToken: () => string | null;
  /** Function to get the map instance (for spatial operations) */
  getMap?: () => mapboxgl.Map | null;
  /**
   * Optional: resolve a user UUID to a display name. Called lazily by
   * models.list / models.get to hydrate Model.userId from an opaque UUID to
   * something Nomad's ModelCard can render (e.g. "Jane Smith").
   *
   * Contract:
   * - return a non-empty string  → adapter replaces Model.userId with that string
   * - return null                → adapter keeps the raw UUID (NOT 'anonymous')
   * - throw                      → adapter keeps the raw UUID (logged, not rethrown)
   *
   * Results are cached per-UUID for the lifetime of the adapter instance.
   * When omitted, Model.userId passes through unchanged (backwards compatible).
   */
  resolveUserName?: (userId: string) => Promise<string | null>;
}

export interface FusionAuthUserInfo {
  sub: string;           // User ID
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  roles?: string[];
}

// =============================================================================
// Role Mapping
// =============================================================================

/**
 * Maps FusionAuth roles to Nomad roles.
 * Adjust this mapping based on your FusionAuth role configuration.
 */
function mapFusionAuthRoleToNomadRole(fusionAuthRoles: string[] = []): UserRole {
  const roles = fusionAuthRoles.map(r => r.toLowerCase());

  if (roles.includes(EM3_ROLES.ADMIN)) return 'admin';
  if (roles.includes('simple_modelling') || roles.includes('fire_behavior_analyst')) return 'fban';
  if (roles.includes('advanced_modelling') || roles.includes('fire_modeler')) return 'modeler';
  if (roles.includes(EM3_ROLES.USER) || roles.includes('viewer')) return 'user';

  return 'user'; // Default role for authenticated users
}

// =============================================================================
// Adapter Factory
// =============================================================================

/**
 * Creates an EM3 adapter for the Nomad Dashboard.
 *
 * @example
 * ```tsx
 * const adapter = createEM3NomadAdapter({
 *   nomadApiUrl: 'https://nomad.nwtfire.ca/api',
 *   getUserInfo: () => fusionAuth.userInfo,
 *   getAccessToken: () => localStorage.getItem('access_token'),
 * });
 *
 * <OpenNomadProvider adapter={adapter}>
 *   <DashboardContainer mode="embedded" />
 * </OpenNomadProvider>
 * ```
 */
export function createEM3NomadAdapter(options: EM3AdapterOptions): IOpenNomadAPI {
  console.log('[EM3NomadAdapter] Creating adapter with options:', {
    nomadApiUrl: options.nomadApiUrl,
    agencyId: options.agencyId,
  });

  const {
    nomadApiUrl,
    agencyId,
    getUserInfo,
    getAccessToken: _getAccessToken,
    getMap,
    resolveUserName,
  } = options;

  /**
   * Per-adapter cache of userId → resolved display name.
   * Stores the promise so concurrent lookups dedupe naturally; resolves to
   * `string` on success and `null` if the resolver returned null or threw.
   */
  const userNameCache = new Map<string, Promise<string | null>>();

  /**
   * Turn a raw Model.userId (possibly undefined/UUID) into what we want to
   * show in the Nomad ModelCard. Never invents a name; only swaps UUID for
   * a resolved display name when we actually have one.
   *
   * - userId absent        → 'anonymous' (unchanged legacy fallback)
   * - no resolver          → pass UUID through
   * - resolver returns str → use it
   * - resolver null/throws → keep UUID
   */
  async function hydrateUserId(userId: string | undefined): Promise<string> {
    if (!userId) return 'anonymous';
    if (!resolveUserName) return userId;

    let lookup = userNameCache.get(userId);
    if (!lookup) {
      lookup = resolveUserName(userId).catch((err) => {
        console.warn(`[EM3NomadAdapter] resolveUserName failed for ${userId}:`, err);
        return null;
      });
      userNameCache.set(userId, lookup);
    }

    const resolved = await lookup;
    return resolved && resolved.length > 0 ? resolved : userId;
  }

  // DrawService instance reference (uses global singleton)
  let drawService: DrawService | null = null;
  let geometryChangeUnsubscribe: (() => void) | null = null;

  // Managed layers for spatial.addLayer/updateLayer/removeLayer
  const managedLayers = new Map<string, MapLayer>();

  // Geometry change callbacks for spatial.onGeometryChange
  const geometryChangeCallbacks = new Set<(geometry: GeoJSONGeometry | null) => void>();

  /**
   * Get or create the DrawService instance (uses global singleton)
   */
  function getDrawService(): DrawService {
    const map = getMap?.();
    if (!map) {
      throw new Error('Map not available. Ensure getMap is provided and returns a valid map instance.');
    }

    // Use global singleton to avoid multiple MapboxDraw controls on the same map
    if (!drawService) {
      drawService = getGlobalDrawService(map);

      // Wire up geometry change notifications (only once)
      if (!geometryChangeUnsubscribe) {
        geometryChangeUnsubscribe = drawService.onGeometryChange((geometry) => {
          geometryChangeCallbacks.forEach((cb) => {
            try {
              cb(geometry as GeoJSONGeometry | null);
            } catch (err) {
              console.error('[EM3NomadAdapter] Error in geometry change callback:', err);
            }
          });
        });
      }
    }

    return drawService;
  }

  /**
   * Get the map instance or throw
   */
  function requireMap(): mapboxgl.Map {
    const map = getMap?.();
    if (!map) {
      throw new Error('Map not available. Ensure getMap is provided and returns a valid map instance.');
    }
    return map;
  }

  // Auth state change listeners
  const authListeners = new Set<(user: User | null) => void>();

  // ==========================================================================
  // Helper: Build headers with agency auth
  // ==========================================================================

  function buildHeaders(): Record<string, string> {
    const userInfo = getUserInfo();
    const accessToken = _getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    if (agencyId) {
      headers['X-Nomad-Agency-Id'] = agencyId;
    }

    if (userInfo) {
      headers['X-Nomad-User-Id'] = userInfo.sub;
      headers['X-Nomad-User-Role'] = mapFusionAuthRoleToNomadRole(userInfo.roles);
      // Always send X-Nomad-User-Name — fallback chain: name → given+family → email → 'Unknown User'
      headers['X-Nomad-User-Name'] = userInfo.name
        || (userInfo.given_name ? `${userInfo.given_name} ${userInfo.family_name || ''}`.trim() : '')
        || userInfo.email
        || 'Unknown User';
    }

    return headers;
  }

  // ==========================================================================
  // Helper: API fetch with error handling
  // ==========================================================================

  async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const url = `${nomadApiUrl}${path}`;
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...buildHeaders(), ...options?.headers },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Nomad API error: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    return response.json();
  }

  // ==========================================================================
  // Auth Module
  // ==========================================================================

  const auth: IOpenNomadAPI['auth'] = {
    async getCurrentUser(): Promise<User | null> {
      const userInfo = getUserInfo();
      if (!userInfo) return null;

      return {
        id: userInfo.sub,
        name: userInfo.name || `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim() || 'Unknown User',
        email: userInfo.email || '',
        role: mapFusionAuthRoleToNomadRole(userInfo.roles),
        agencyId,
      };
    },

    async getAuthToken(): Promise<string | null> {
      // In ACN mode, we don't pass the FusionAuth token to Nomad
      // Instead, we use agency credentials. Return null to indicate this.
      return null;
    },

    onAuthChange(callback: (user: User | null) => void): Unsubscribe {
      authListeners.add(callback);
      return () => authListeners.delete(callback);
    },
  };

  // ==========================================================================
  // Models Module
  // ==========================================================================

  const models: IOpenNomadAPI['models'] = {
    /**
     * Create a model in Nomad.
     *
     * This method handles field mapping between EM3's types and Nomad's backend API:
     * - Sends only `name` and `engineType` to POST /models (creates draft)
     * - Stores execution params (geometry, temporal, weather, output) for jobs.submit
     *
     * The params object can include EM3-style fields which are transformed:
     * - `engineType` -> `engineType` (direct mapping)
     * - `geometry` -> stored as `ignition` for execute call
     * - `temporal` -> stored as `timeRange` for execute call
     * - `weather` -> stored as `weather` for execute call
     * - `output` -> stored as flat fields for execute call
     */
    async create(params: ModelCreateParams & {
      engineType?: string;
      geometry?: GeoJSONGeometry;
      temporal?: { startDate: string; startTime: string; durationHours: number; timezone: string };
      output?: { mode?: string };
    }): Promise<Model> {
      console.log('[EM3NomadAdapter] models.create called with:', params);

      // Extract engine type (handle both 'engine' and 'engineType' field names)
      const engineType = (params as { engineType?: string }).engineType || params.engine;

      // Extract and transform execution params for later use by jobs.submit
      const extParams = params as {
        geometry?: GeoJSONGeometry;
        temporal?: { startDate: string; startTime: string; durationHours: number; timezone: string };
        weather?: { source: string; startingCodes?: { ffmc: number; dmc: number; dc: number }; firestarrCsvContent?: string; rawWeatherContent?: string; spotwxCsvData?: string };
        output?: { mode?: string };
      };

      // Build the create payload
      // Note: userId comes from X-Nomad-User-Id header (set by buildHeaders),
      // outputMode is set during execution (POST /models/:id/execute)
      const createPayload: Record<string, unknown> = {
        name: params.name,
        engineType,
      };
      if (params.notes !== undefined) {
        createPayload.notes = params.notes;
      }

      console.log('[EM3NomadAdapter] Sending create payload:', createPayload);

      if (extParams.geometry && extParams.temporal) {
        // Transform geometry to ignition format
        const geomType = extParams.geometry.type;
        let ignitionType: 'point' | 'polygon' = 'point';
        let coordinates: [number, number] | [number, number][][];

        if (geomType === 'Point') {
          ignitionType = 'point';
          coordinates = (extParams.geometry as GeoJSON.Point).coordinates as [number, number];
        } else if (geomType === 'Polygon') {
          ignitionType = 'polygon';
          // Polygon coordinates are [[[lng, lat], ...]] - keep full structure (array of rings)
          // Backend SpatialGeometry.validatePolygon expects Position[][] format
          coordinates = (extParams.geometry as GeoJSON.Polygon).coordinates as [number, number][][];
        } else {
          // For other types (LineString, etc.), wrap in polygon format
          ignitionType = 'polygon';
          const lineCoords = (extParams.geometry as GeoJSON.LineString).coordinates as [number, number][];
          // Close the ring if needed
          const closed = lineCoords[0][0] === lineCoords[lineCoords.length - 1][0] &&
                         lineCoords[0][1] === lineCoords[lineCoords.length - 1][1]
            ? lineCoords
            : [...lineCoords, lineCoords[0]];
          coordinates = [closed];
        }

        // Transform temporal to timeRange
        // Combine date and time, then calculate end from duration
        const startDateTime = `${extParams.temporal.startDate}T${extParams.temporal.startTime}:00`;
        const startDate = new Date(startDateTime);
        const endDate = new Date(startDate.getTime() + extParams.temporal.durationHours * 60 * 60 * 1000);

        // #700: Derive the backend weather source from the content actually
        // provided, not from the wizard's lossy 'source' label (which collapses
        // both firestarr_csv and raw_weather uploads to 'csv'). The dashboard
        // reads an uploaded file into exactly one of firestarrCsvContent /
        // rawWeatherContent / spotwxCsvData; a live SpotWX fetch has none.
        let weatherSource: 'firestarr_csv' | 'raw_weather' | 'spotwx' = 'spotwx';
        let rawWeatherContent = extParams.weather?.rawWeatherContent;
        if (extParams.weather?.firestarrCsvContent) {
          weatherSource = 'firestarr_csv';
        } else if (rawWeatherContent) {
          weatherSource = 'raw_weather';
        }

        // #694: An UPLOADED SpotWX forecast carries its CSV text on spotwxCsvData.
        // Run it through openNomad's shared resolveWeatherParams so it normalizes
        // to raw_weather — the backend then runs it WITHOUT a SPOTWX_API_KEY.
        // Takes precedence: a SpotWX upload also produces raw weather content.
        if (extParams.weather?.spotwxCsvData) {
          const resolved = resolveWeatherParams({
            source: 'spotwx',
            spotwxCsvData: extParams.weather.spotwxCsvData,
            startingCodes: extParams.weather.startingCodes,
          });
          weatherSource = resolved.source;
          rawWeatherContent = resolved.rawWeatherContent;
        }

        // Store execution params for jobs.submit
        pendingExecutionParams = {
          ignition: {
            type: ignitionType,
            coordinates,
          },
          timeRange: {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
          },
          timezone: extParams.temporal.timezone,
          weather: {
            source: weatherSource,
            startingCodes: extParams.weather?.startingCodes,
            firestarrCsvContent: extParams.weather?.firestarrCsvContent,
            rawWeatherContent,
          },
          outputMode: (() => {
            if (extParams.output?.mode) {
              return extParams.output.mode as 'probabilistic' | 'deterministic';
            }
            console.info('[EM3NomadAdapter] outputMode not specified, defaulting to probabilistic');
            return 'probabilistic' as const;
          })(),
        };

        // DEBUG: outputMode bug investigation
        console.log('[EM3NomadAdapter] ========== OUTPUT MODE DEBUG ==========');
        console.log('[EM3NomadAdapter] extParams.output:', extParams.output);
        console.log('[EM3NomadAdapter] extParams.output?.mode (raw):', extParams.output?.mode);
        console.log('[EM3NomadAdapter] pendingExecutionParams.outputMode (stored):', pendingExecutionParams.outputMode);
        console.log('[EM3NomadAdapter] ========================================');
        console.log('[EM3NomadAdapter] Stored execution params:', pendingExecutionParams);
      }

      try {
        const result = await apiFetch<Model>('/models', {
          method: 'POST',
          body: JSON.stringify(createPayload),
        });
        console.log('[EM3NomadAdapter] models.create success:', result);
        return result;
      } catch (error) {
        console.error('[EM3NomadAdapter] models.create error:', error);
        throw error;
      }
    },

    async list(
      filter?: ModelFilter,
      pagination?: PaginationParams
    ): Promise<PaginatedResponse<Model>> {
      // Nomad backend returns { models: [...], total: N }
      // We need to map this to PaginatedResponse<Model>
      interface BackendResponse {
        models: Array<{
          id: string;
          name: string;
          engineType: string;
          status: string;
          createdAt: string;
          updatedAt?: string;
          userId?: string;
          outputMode?: string | null;
          notes?: string;
        }>;
        total: number;
      }

      const response = await apiFetch<BackendResponse>('/models');

      for (const m of response.models) {
        if (!m.userId) {
          console.warn(`[EM3NomadAdapter] models.list: model ${m.id} missing userId — falling back to 'anonymous'`);
        }
      }

      // Resolve userIds to display names up front. Same UUID across multiple
      // models shares one cache entry, and subsequent list() calls skip the
      // resolver entirely for UUIDs we've already hydrated.
      const hydratedUserIds = await Promise.all(
        response.models.map((m) => hydrateUserId(m.userId)),
      );

      // Map backend model format to openNomad Model type
      let models: Model[] = response.models.map((m, idx) => ({
        id: m.id,
        name: m.name,
        engine: m.engineType as Model['engine'],
        status: m.status as ModelStatus,
        userId: hydratedUserIds[idx],
        createdAt: m.createdAt,
        updatedAt: m.updatedAt || m.createdAt,
        outputMode: m.outputMode || null,
        notes: m.notes,
      }));

      // Apply client-side filtering (backend doesn't support all filters)
      if (filter) {
        if (filter.status) {
          const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
          models = models.filter((m) => statuses.includes(m.status));
        }
        if (filter.engine) {
          models = models.filter((m) => m.engine === filter.engine);
        }
        if (filter.search) {
          const search = filter.search.toLowerCase();
          models = models.filter((m) => m.name.toLowerCase().includes(search));
        }
      }

      // Apply pagination
      const page = pagination?.page ?? 1;
      const limit = pagination?.limit ?? 20;
      const startIndex = (page - 1) * limit;
      const paginatedModels = models.slice(startIndex, startIndex + limit);

      return {
        data: paginatedModels,
        total: models.length,
        page,
        limit,
        totalPages: Math.ceil(models.length / limit),
      };
    },

    async get(id: string): Promise<Model> {
      // Backend returns engineType, we need engine
      interface BackendModel {
        id: string;
        name: string;
        engineType: string;
        status: string;
        createdAt: string;
        updatedAt?: string;
        userId?: string;
        notes?: string;
        outputMode?: string | null;
      }
      const m = await apiFetch<BackendModel>(`/models/${id}`);
      if (!m.userId) {
        console.warn(`[EM3NomadAdapter] models.get: model ${m.id} missing userId — falling back to 'anonymous'`);
      }
      return {
        id: m.id,
        name: m.name,
        engine: m.engineType as Model['engine'],
        status: m.status as ModelStatus,
        userId: await hydrateUserId(m.userId),
        createdAt: m.createdAt,
        updatedAt: m.updatedAt || m.createdAt,
        notes: m.notes,
        outputMode: m.outputMode || null,
      };
    },

    async update(id: string, updates: Partial<ModelCreateParams>): Promise<Model> {
      return apiFetch<Model>(`/models/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
    },

    async delete(id: string): Promise<void> {
      await apiFetch(`/models/${id}`, { method: 'DELETE' });
    },

    async getStatus(id: string): Promise<ModelStatus> {
      const model = await this.get(id);
      return model.status;
    },
  };

  // ==========================================================================
  // Jobs Module
  // ==========================================================================

  const jobSubscriptions = new Map<string, {
    callbacks: Set<(status: JobStatusDetail) => void>;
    intervalId: ReturnType<typeof setInterval>;
  }>();

  // Store execution params for jobs.submit (set by models.create wrapper)
  let pendingExecutionParams: ExecutionParams | null = null;

  /**
   * Execution parameters expected by Nomad backend POST /models/:id/execute
   */
  interface ExecutionParams {
    ignition: {
      type: 'point' | 'polygon';
      // Point: [lon, lat], Polygon: [[[lon, lat], ...]] (array of rings per GeoJSON spec)
      coordinates: [number, number] | [number, number][][];
    };
    timeRange: {
      start: string;
      end: string;
    };
    /**
     * IANA timezone identifier (e.g. "America/Yellowknife"). Required by the
     * Nomad /execute endpoint — the backend rejects the request with
     * VALIDATION_ERROR: "Timezone required" if this is absent. No silent
     * local default — the wizard collects this in the Temporal step and we
     * pass it straight through.
     */
    timezone?: string;
    weather: {
      source: 'firestarr_csv' | 'raw_weather' | 'spotwx';
      firestarrCsvContent?: string;
      rawWeatherContent?: string;
      startingCodes?: { ffmc: number; dmc: number; dc: number };
      latitude?: number;
    };
    scenarios?: number;
    outputMode?: 'probabilistic' | 'deterministic';
  }

  const jobs: IOpenNomadAPI['jobs'] = {
    async submit(modelId: string): Promise<JobSubmitResponse> {
      console.log('[EM3NomadAdapter] jobs.submit called for model:', modelId);

      if (!pendingExecutionParams) {
        throw new Error('No execution params set. Call models.create first with full params.');
      }

      console.log('[EM3NomadAdapter] Sending execution params:', pendingExecutionParams);

      try {
        const result = await apiFetch<JobSubmitResponse>(`/models/${modelId}/execute`, {
          method: 'POST',
          body: JSON.stringify(pendingExecutionParams),
        });
        console.log('[EM3NomadAdapter] jobs.submit success:', result);
        // Clear pending params after successful submission
        pendingExecutionParams = null;
        return result;
      } catch (error) {
        console.error('[EM3NomadAdapter] jobs.submit error:', error);
        throw error;
      }
    },

    async cancel(jobId: string): Promise<void> {
      await apiFetch(`/jobs/${jobId}/cancel`, { method: 'POST' });
    },

    async getStatus(jobId: string): Promise<JobStatusDetail> {
      return apiFetch<JobStatusDetail>(`/jobs/${jobId}`);
    },

    onStatusChange(
      jobId: string,
      callback: (status: JobStatusDetail) => void
    ): Unsubscribe {
      // Polling implementation (can be upgraded to WebSocket later)
      let subscription = jobSubscriptions.get(jobId);

      if (!subscription) {
        const callbacks = new Set<(status: JobStatusDetail) => void>();

        const poll = async () => {
          try {
            const status = await jobs.getStatus(jobId);
            callbacks.forEach((cb) => cb(status));

            // Stop polling on terminal state
            if (['completed', 'failed', 'cancelled'].includes(status.status)) {
              const sub = jobSubscriptions.get(jobId);
              if (sub) {
                clearInterval(sub.intervalId);
                jobSubscriptions.delete(jobId);
              }
            }
          } catch (error) {
            console.error(`[EM3NomadAdapter] Error polling job ${jobId}:`, error);
          }
        };

        const intervalId = setInterval(poll, 3000);
        poll(); // Immediate first poll

        subscription = { callbacks, intervalId };
        jobSubscriptions.set(jobId, subscription);
      }

      subscription.callbacks.add(callback);

      return () => {
        const sub = jobSubscriptions.get(jobId);
        if (sub) {
          sub.callbacks.delete(callback);
          if (sub.callbacks.size === 0) {
            clearInterval(sub.intervalId);
            jobSubscriptions.delete(jobId);
          }
        }
      };
    },
  };

  // ==========================================================================
  // Results Module
  // ==========================================================================

  const results: IOpenNomadAPI['results'] = {
    async get(modelId: string): Promise<ModelResults> {
      return apiFetch<ModelResults>(`/models/${modelId}/results`);
    },

    async getData(resultIdOrUrl: string): Promise<GeoJSONGeometry | string> {
      // Handle downloadUrl (relative path from API response) vs result ID
      let url: string;
      if (resultIdOrUrl.startsWith('/api/v1/')) {
        // It's a Nomad API path - strip /api/v1 prefix and route through our proxy
        // e.g., /api/v1/results/xxx/download -> nomadApiUrl + /results/xxx/download
        const relativePath = resultIdOrUrl.replace('/api/v1', '');
        url = `${nomadApiUrl}${relativePath}`;
        console.log(`[EM3NomadAdapter] getData: rewriting ${resultIdOrUrl} -> ${url}`);
      } else if (resultIdOrUrl.startsWith('/')) {
        // Other relative paths - resolve against nomadApiUrl origin
        const baseOrigin = new URL(nomadApiUrl).origin;
        url = `${baseOrigin}${resultIdOrUrl}`;
      } else if (resultIdOrUrl.startsWith('http')) {
        // It's an absolute URL - use as-is
        url = resultIdOrUrl;
      } else {
        // Legacy: treat as result ID
        url = `${nomadApiUrl}/results/${resultIdOrUrl}/data`;
      }

      const response = await fetch(url, {
        credentials: 'include',
        headers: buildHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch result data: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';
      console.log('[EM3NomadAdapter] getData response', { url, contentType, status: response.status });

      // Handle JSON variants: application/json, application/geo+json, text/json, text/plain (often used for GeoJSON)
      if (contentType.includes('json') || contentType.includes('geojson') || contentType.includes('text/plain')) {
        return response.json();
      }

      // If content-type is missing or unknown, try to parse as JSON anyway
      if (!contentType || contentType === 'application/octet-stream') {
        try {
          return await response.json();
        } catch {
          // Not JSON, return URL for binary data
          return response.url;
        }
      }

      // Return URL for raster/binary data
      return response.url;
    },

    async export(modelId: string, params: ExportParams): Promise<Blob> {
      // Translate bundle's ExportParams to Nomad backend's expected shape:
      //   Bundle sends:  { resultIds: ["uuid"], format: "geojson" }
      //   Backend wants: { modelId: "uuid", items: [{ resultId: "uuid", format: "geojson" }] }
      const nomadPayload = {
        modelId,
        items: (params.resultIds || []).map(resultId => ({
          resultId,
          format: params.format,
        })),
      };

      console.log('[EM3NomadAdapter] export: translated payload', nomadPayload);

      // Step 1: Create export job
      const exportResult = await apiFetch<{ exportId: string; manifest: unknown }>(
        '/exports',
        {
          method: 'POST',
          body: JSON.stringify(nomadPayload),
        }
      );

      console.log('[EM3NomadAdapter] export: created export', exportResult.exportId);

      // Step 2: Create shareable download link (no auth required for download)
      const shareResult = await apiFetch<{ shareUrl: string; token: string }>(
        `/exports/${exportResult.exportId}/share`,
        { method: 'POST' }
      );

      console.log('[EM3NomadAdapter] export: share URL', shareResult.shareUrl);

      // Step 3: Download via share URL (no auth needed)
      const downloadResponse = await fetch(shareResult.shareUrl);

      if (!downloadResponse.ok) {
        throw new Error(`Failed to download export: ${downloadResponse.status}`);
      }

      return downloadResponse.blob();
    },

    async getExportFormats(): Promise<ExportFormat[]> {
      // GNWT-supported export formats
      return [
        {
          id: 'geojson',
          name: 'GeoJSON',
          extension: 'geojson',
          category: 'vector',
          supportedOutputTypes: ['perimeter', 'probability'],
        },
        {
          id: 'shapefile',
          name: 'Shapefile',
          extension: 'shp',
          category: 'vector',
          supportedOutputTypes: ['perimeter'],
        },
        {
          id: 'kml',
          name: 'KML (Google Earth)',
          extension: 'kml',
          category: 'vector',
          supportedOutputTypes: ['perimeter'],
        },
        {
          id: 'geotiff',
          name: 'GeoTIFF',
          extension: 'tif',
          category: 'raster',
          supportedOutputTypes: ['probability', 'intensity', 'rate_of_spread', 'arrival_time'],
        },
      ];
    },

    // ========================================================================
    // URL Generation Methods (for adapter pattern - avoids hardcoded URLs)
    // ========================================================================

    getModelResultsUrl(modelId: string): string {
      const url = `${nomadApiUrl}/models/${modelId}/results`;
      console.log('[EM3NomadAdapter] getModelResultsUrl called:', { modelId, nomadApiUrl, url });
      return url;
    },

    getPreviewUrl(resultId: string, mode?: 'static' | 'dynamic'): string {
      const baseUrl = `${nomadApiUrl}/results/${resultId}/preview`;
      return mode ? `${baseUrl}?mode=${mode}` : baseUrl;
    },

    getDownloadUrl(resultId: string): string {
      return `${nomadApiUrl}/results/${resultId}/download`;
    },

    getTileUrlTemplate(resultId: string): string {
      return `${nomadApiUrl}/results/${resultId}/tile/{z}/{x}/{y}.png`;
    },

    async getTileBounds(resultId: string): Promise<BBox> {
      try {
        const result = await apiFetch<{ bounds: BBox }>(`/results/${resultId}/bounds`);
        if (result.bounds) {
          return result.bounds;
        }
      } catch (error) {
        console.warn(`[EM3NomadAdapter] Could not fetch bounds for result ${resultId}:`, error);
      }
      // Return NWT default bounds as fallback
      return [-141.0, 60.0, -102.0, 78.0];
    },
  };

  // ==========================================================================
  // Spatial Module
  // ==========================================================================

  const spatial: IOpenNomadAPI['spatial'] = {
    // Host-provided map services (#638/Nomad 0.11): EM3 always has a host map,
    // so it supplies spatial.map. SAN deployments without a map omit it.
    map: {
    // ========================================================================
    // Drawing Methods
    // ========================================================================

    drawPoint(): Promise<GeoJSON.Point> {
      return getDrawService().drawPoint();
    },

    drawLine(): Promise<GeoJSON.LineString> {
      return getDrawService().drawLine();
    },

    drawPolygon(): Promise<GeoJSON.Polygon> {
      return getDrawService().drawPolygon();
    },

    onGeometryChange(callback: (geometry: GeoJSONGeometry | null) => void): Unsubscribe {
      geometryChangeCallbacks.add(callback);
      return () => {
        geometryChangeCallbacks.delete(callback);
      };
    },

    cancelDraw(): void {
      if (drawService) {
        drawService.cancelDraw();
      }
    },

    // ========================================================================
    // Layer Management Methods
    // ========================================================================

    addLayer(layer: MapLayer): void {
      const map = requireMap();
      const sourceId = `nomad-layer-${layer.id}`;
      const layerId = `nomad-layer-${layer.id}`;

      // Store the layer config
      managedLayers.set(layer.id, layer);

      // Add source if it's GeoJSON
      if (layer.type === 'geojson' && layer.data) {
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'geojson',
            data: layer.data as GeoJSON.FeatureCollection,
          });
        }

        // Determine geometry type for styling
        const geomType = getGeometryType(layer.data);
        const style = layer.style || {};

        if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
          // Add fill layer
          if (!map.getLayer(`${layerId}-fill`)) {
            map.addLayer({
              id: `${layerId}-fill`,
              type: 'fill',
              source: sourceId,
              paint: {
                'fill-color': style.fillColor || '#FF6B00',
                'fill-opacity': style.fillOpacity ?? 0.3,
              },
            });
          }
          // Add stroke layer
          if (!map.getLayer(`${layerId}-stroke`)) {
            map.addLayer({
              id: `${layerId}-stroke`,
              type: 'line',
              source: sourceId,
              paint: {
                'line-color': style.strokeColor || '#FF6B00',
                'line-width': style.strokeWidth ?? 2,
                'line-opacity': style.strokeOpacity ?? 1,
              },
            });
          }
        } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
          if (!map.getLayer(layerId)) {
            map.addLayer({
              id: layerId,
              type: 'line',
              source: sourceId,
              paint: {
                'line-color': style.strokeColor || '#FF6B00',
                'line-width': style.strokeWidth ?? 2,
                'line-opacity': style.strokeOpacity ?? 1,
              },
            });
          }
        } else if (geomType === 'Point' || geomType === 'MultiPoint') {
          if (!map.getLayer(layerId)) {
            map.addLayer({
              id: layerId,
              type: 'circle',
              source: sourceId,
              paint: {
                'circle-radius': style.pointRadius ?? 6,
                'circle-color': style.pointColor || style.fillColor || '#FF6B00',
                'circle-stroke-color': style.strokeColor || '#FFF',
                'circle-stroke-width': style.strokeWidth ?? 2,
              },
            });
          }
        }
      } else if (layer.type === 'raster' && layer.url) {
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, {
            type: 'raster',
            tiles: [layer.url],
            tileSize: 256,
          });
        }
        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            type: 'raster',
            source: sourceId,
          });
        }
      }

      // Handle visibility
      if (layer.visible === false) {
        const layers = map.getStyle().layers || [];
        layers.forEach((l) => {
          if (l.id.startsWith(layerId)) {
            map.setLayoutProperty(l.id, 'visibility', 'none');
          }
        });
      }
    },

    updateLayer(id: string, updates: Partial<MapLayer>): void {
      const map = requireMap();
      const existing = managedLayers.get(id);
      if (!existing) {
        console.warn(`[EM3NomadAdapter] Layer ${id} not found for update`);
        return;
      }

      const updated = { ...existing, ...updates };
      managedLayers.set(id, updated);

      const sourceId = `nomad-layer-${id}`;
      const layerId = `nomad-layer-${id}`;

      // Update source data if changed
      if (updates.data && updated.type === 'geojson') {
        const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
        if (source) {
          source.setData(updates.data as GeoJSON.FeatureCollection);
        }
      }

      // Update visibility if changed
      if (updates.visible !== undefined) {
        const visibility = updates.visible ? 'visible' : 'none';
        const layers = map.getStyle().layers || [];
        layers.forEach((l) => {
          if (l.id.startsWith(layerId)) {
            map.setLayoutProperty(l.id, 'visibility', visibility);
          }
        });
      }

      // Update style properties if changed
      if (updates.style) {
        const style = updates.style;
        const layers = map.getStyle().layers || [];
        layers.forEach((l) => {
          if (!l.id.startsWith(layerId)) return;

          if (l.type === 'fill') {
            if (style.fillColor) map.setPaintProperty(l.id, 'fill-color', style.fillColor);
            if (style.fillOpacity !== undefined) map.setPaintProperty(l.id, 'fill-opacity', style.fillOpacity);
          } else if (l.type === 'line') {
            if (style.strokeColor) map.setPaintProperty(l.id, 'line-color', style.strokeColor);
            if (style.strokeWidth !== undefined) map.setPaintProperty(l.id, 'line-width', style.strokeWidth);
            if (style.strokeOpacity !== undefined) map.setPaintProperty(l.id, 'line-opacity', style.strokeOpacity);
          } else if (l.type === 'circle') {
            if (style.pointRadius !== undefined) map.setPaintProperty(l.id, 'circle-radius', style.pointRadius);
            if (style.pointColor) map.setPaintProperty(l.id, 'circle-color', style.pointColor);
          }
        });
      }
    },

    removeLayer(id: string): void {
      const map = requireMap();
      const sourceId = `nomad-layer-${id}`;
      const layerId = `nomad-layer-${id}`;

      // Remove from managed layers
      managedLayers.delete(id);

      // Remove all layers with this prefix
      const layers = map.getStyle().layers || [];
      layers.forEach((l) => {
        if (l.id.startsWith(layerId)) {
          map.removeLayer(l.id);
        }
      });

      // Remove source
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    },

    // ========================================================================
    // Map Navigation Methods
    // ========================================================================

    fitBounds(bounds: BBox, options?: { padding?: number; animate?: boolean }): void {
      const map = requireMap();
      const [minLng, minLat, maxLng, maxLat] = bounds;

      map.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        {
          padding: options?.padding ?? 50,
          animate: options?.animate ?? true,
        }
      );
    },

    getBounds(): BBox {
      const map = requireMap();
      const bounds = map.getBounds();
      if (!bounds) {
        // Return NWT default bounds if map bounds not yet available
        return [-141.0, 60.0, -102.0, 78.0];
      }
      return [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];
    },

    }, // end spatial.map

    // ========================================================================
    // Data Services (required, queryable without a host map)
    // ========================================================================
    data: {

    async getWeatherStations(bounds: BBox): Promise<WeatherStation[]> {
      // TODO: Integrate with EM3's WeatherStationService
      // For now, proxy to Nomad's spatial service
      const [minLng, minLat, maxLng, maxLat] = bounds;
      return apiFetch<WeatherStation[]>(
        `/spatial/weather-stations?bbox=${minLng},${minLat},${maxLng},${maxLat}`
      );
    },

    async getFuelTypes(bounds: BBox): Promise<FuelTypeData> {
      // GNWT fuel type configuration
      // Could also fetch from EM3's GeoServer
      return {
        bounds,
        fuelTypes: [
          { code: 'C-1', name: 'Spruce–Lichen Woodland', color: '#006400' },
          { code: 'C-2', name: 'Boreal Spruce', color: '#228B22' },
          { code: 'C-3', name: 'Mature Jack or Lodgepole Pine', color: '#32CD32' },
          { code: 'C-4', name: 'Immature Jack or Lodgepole Pine', color: '#90EE90' },
          { code: 'C-5', name: 'Red and White Pine', color: '#98FB98' },
          { code: 'C-6', name: 'Conifer Plantation', color: '#00FF00' },
          { code: 'C-7', name: 'Ponderosa Pine–Douglas-Fir', color: '#7CFC00' },
          { code: 'D-1', name: 'Leafless Aspen', color: '#FFD700' },
          { code: 'D-2', name: 'Green Aspen (with BUI Thresholding)', color: '#FFA500' },
          { code: 'M-1', name: 'Boreal Mixedwood–Leafless', color: '#8FBC8F' },
          { code: 'M-2', name: 'Boreal Mixedwood–Green', color: '#3CB371' },
          { code: 'M-3', name: 'Dead Balsam Fir Mixedwood–Leafless', color: '#2E8B57' },
          { code: 'M-4', name: 'Dead Balsam Fir Mixedwood–Green', color: '#20B2AA' },
          { code: 'S-1', name: 'Jack or Lodgepole Pine Slash', color: '#8B4513' },
          { code: 'S-2', name: 'White Spruce–Balsam Slash', color: '#A0522D' },
          { code: 'S-3', name: 'Coastal Cedar–Hemlock–Douglas-Fir Slash', color: '#CD853F' },
          { code: 'O-1a', name: 'Standing Grass', color: '#F0E68C' },
          { code: 'O-1b', name: 'Matted Grass', color: '#EEE8AA' },
        ],
        serviceUrl: 'https://geoserver.nwtfire.ca/geoserver/wcs',
        layerName: 'intellifire:fuels',
      };
    },

    async getElevation(bounds: BBox): Promise<ElevationData> {
      // GNWT DEM service
      return {
        bounds,
        serviceUrl: 'https://geoserver.nwtfire.ca/geoserver/wcs',
        resolutionM: 30,
        elevationRange: {
          min: 0,
          max: 2773, // Highest point in NWT
        },
      };
    },
    }, // end spatial.data
  };

  // ==========================================================================
  // Helper: Get geometry type from GeoJSON
  // ==========================================================================

  function getGeometryType(data: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry): string {
    if ('type' in data) {
      if (data.type === 'FeatureCollection') {
        return data.features[0]?.geometry?.type || 'Unknown';
      } else if (data.type === 'Feature') {
        return data.geometry?.type || 'Unknown';
      } else {
        return data.type;
      }
    }
    return 'Unknown';
  }

  // ==========================================================================
  // Config Module
  // ==========================================================================

  const config: IOpenNomadAPI['config'] = {
    async getAvailableEngines(): Promise<Engine[]> {
      // Engines available to GNWT
      return [
        {
          id: 'firestarr',
          name: 'FireSTARR',
          description: 'Stochastic fire spread modeling with probabilistic outputs',
          available: true,
          capabilities: {
            probabilistic: true,
            deterministic: true,
            polygonIgnition: true,
            lineIgnition: true,
            maxDurationHours: 720,
          },
        },
        // WISE can be added when available
        // {
        //   id: 'wise',
        //   name: 'WISE',
        //   description: 'Wildfire Intelligence & Scenario Engine',
        //   available: false,
        //   capabilities: {...},
        // },
      ];
    },

    async getAgencyConfig(): Promise<AgencyConfig> {
      return {
        id: 'gnwt',
        name: 'Government of the Northwest Territories',
        logoUrl: '/assets/gnwt-logo.png',
        branding: {
          primaryColor: '#00533E', // GNWT green
          secondaryColor: '#FFB81C', // GNWT gold
        },
        defaultMapCenter: {
          lat: 64.2667,
          lng: -114.3667,
          zoom: 5,
        },
        exportFormats: await results.getExportFormats(),
        contact: {
          name: 'NWT Fire Operations',
          email: 'fire_info@gov.nt.ca',
        },
      };
    },
  };

  // ==========================================================================
  // Return Complete Adapter
  // ==========================================================================

  return {
    fetch: async (url: string, init?: RequestInit): Promise<Response> => {
      const headers = buildHeaders();
      const mergedHeaders: Record<string, string> = { ...headers, ...(init?.headers as Record<string, string>) };
      // For FormData bodies (e.g. Nomad's model-import ZIP upload), the browser
      // must set Content-Type to `multipart/form-data; boundary=...` itself.
      // Pinning it to application/json here causes EM3's express.json() body
      // parser to try to parse the multipart bytes as JSON and reject with 413
      // Payload Too Large.
      if (typeof FormData !== 'undefined' && init?.body instanceof FormData) {
        const callerSetContentType = init.headers && (init.headers as Record<string, string>)['Content-Type'];
        if (!callerSetContentType) {
          delete mergedHeaders['Content-Type'];
        }
      }
      // Resolve relative URLs against our proxy base URL
      const resolvedUrl = url.startsWith('http') ? url : `${nomadApiUrl}${url.startsWith('/') ? url.replace(/^\/api\/v1/, '') : '/' + url}`;
      return fetch(resolvedUrl, { ...init, credentials: 'include', headers: mergedHeaders });
    },
    getBaseUrl: () => nomadApiUrl,
    auth,
    models,
    jobs,
    results,
    spatial,
    config,
  };
}
